<template>
  <div :class="boxClassName">
    <div class="box-title" style="width: calc(100% - 100px)">
      <el-row type="flex" justify="space-between">
          <slot name="title" />
          <div v-show="showDirectionSwitch">
            IN <el-switch v-model="direction" inactive-color="#409EFF" active-value="output" inactive-value="input" @change="onChangeCardDirection"/> OUT
          </div>
      </el-row>
    </div>
    <el-tabs type="border-card" :class="tabClassName" v-loading="loading">
      <el-tab-pane style="height: 220px;">
        <span slot="label"><i class="fas fa-chart-bar" /></span>
        <w-smart-trace-pie-chart ref="smartChart" :trace-data="traceData" :type="smartTraceFilterType" @selectItem="onSelectItem" />
      </el-tab-pane>
      <el-tab-pane style="height: 220px;">
        <span slot="label"><i class="fas fa-bars" /></span>
        <w-smart-trace-table :trace-data="traceData" :cardType="cardType" :unitType="unitType" />
      </el-tab-pane>
    </el-tabs>
    <div class="box-footer">
      <div class="num">
        <slot name="number" />
      </div>
      <div class="text-area">
        {{ selectedItem.name }}
        <p>{{ `${unitType} ${selectedItem.value}` }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import clone from 'lodash/clone';
import objectIsEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import find from 'lodash/find';
import { isEmpty } from '@/utils/basic';
import { convertValuByUnit } from '@/utils/number';
import MsgUtil from '@/utils/message';
import FlowAPI from '@/api/performance/flow-analysis/flow-analysis';
import WSmartTracePieChart from '../chart/SmartTracePieChart';
import WSmartTraceTable from '../table/SmartTraceTable';

export default {
  name: 'WSmartTraceComponent',
  components: {
    WSmartTracePieChart,
    WSmartTraceTable,
  },
  props: {
    flowParams: Object,
    cardType: String,
    firstCardType: String,
    analysisType: String,
    selectedTraceFilter: Object,
    selectedItems: Array,
  },
  data() {
    return {
      direction: 'input',
      requestUrl: {
        ipaddress: 'getIpSmartTraceData',
        protocol: 'getProtocolSmartTraceData',
        port: 'getPortSmartTraceData',
        netinterface: 'getInterfaceSmartTraceData',
        conversation: 'getConversationSmartTraceData',
      },
      traceData: [],
      initSelectedItem: {
        selected: false,
        name: '전체',
        value: '-',
      },
      selectedItem: {},
      smartTraceFilterType: null,
      smartTraceParams: {},
      smartTraceFilter: {},
      loading: false,
      loadType: null,
    };
  },
  computed: {
    showDirectionSwitch() {
      const type = this.cardType;
      return type === 'ipaddress' || type === 'port' || type === 'netinterface';
    },
    unitType() {
      const flowParams = this.flowParams;
      if (isEmpty(flowParams) || isEmpty(flowParams.requestParamVO)) {
        return 'Bytes';
      }

      const meter = flowParams.requestParamVO.meter;
      if (meter === 'octets') {
        return 'Bytes';
      } else if (meter === 'packets') {
        return 'Packets';
      }
      return meter;
    },
    boxClassName() {
      const selectedItem = this.selectedItem;
      // 항목 선택 시 flipped-border 미선택시 shadow-none
      let className = 'box-wrapper box-tab';
      if (objectIsEmpty(selectedItem) || !selectedItem.selected) {
        className += ' shadow-none';
      } else {
        className += ' flipped-border';
      }
      return className;
    },
    tabClassName() {
      const selectedItem = this.selectedItem;
      return objectIsEmpty(selectedItem) || !selectedItem.selected ? 'no-shadow' : null;
    },
    /**
     * 첫 번째 카드가 Conversation이 아닌 경우 IP의 방향에 따라 cardType을 결정하기 위한 data
     * @return {Boolean} [description]
     * @author jda
     */
    isConversation() {
      return this.firstCardType !== 'conversation' && this.cardType === 'conversation';
    },
  },
  watch: {
    flowParams(params) {
      this.resetSelectedItem();
      if (objectIsEmpty(params)) {
        this.traceData = [];
        this.initSelectedItem.value = '-';
        return;
      } else if (isEmpty(params.requestParamVO)) {
        return;
      }

      this.loadType = 'flow';
      this.smartTraceFilter = {};
      // this.smartTraceFilter = params.requestParamVO.smartTraceFilter;
      this.loadData();
    },
    cardType: {
      immediate: true,
      handler(cardType) {
        // 차트 분석 타입이 바뀔경우 데이터 두번 로드되는 것을 막는다.
        if (this.analysisType !== this.smartTraceParams.analysisType) {
          return;
        } else if (this.analysisType === 'netdevice') {
          // [장비별 분석] 스마트 추적 초기화 일 경우
          if (isEmpty(this.selectedItems)) {
            this.smartTraceFilter = {};
            this.resetSelectedItem();
          } else {
            // [장비별 분석] 스마트 추적 선택 항목이 하나라도 있을 경우
            this.setAnalysisFilterByCardType();

            const type = this.smartTraceFilterType;
            const selectedItem = find(this.selectedItems, { type });
            if (objectIsEmpty(selectedItem)) {
              this.resetSelectedItem();
            } else {
              this.selectedItem = selectedItem;
            }

            // 인터페이스 카드는 필터링 없이 로드
            if (cardType === 'netinterface') {
              this.smartTraceFilter = {};
            } else {
              const smartTraceFilter = {};
              const selectedItems = this.selectedItems;
              for (let i = 0; i < selectedItems.length; i += 1) {
                const item = selectedItems[i];
                if (item.selected) {
                  if (item.type === type) {
                    break;
                  }
                  smartTraceFilter[item.type] = item.itemId;
                }
              }
              this.smartTraceFilter = smartTraceFilter;
            }
          }
        } else {
          // 선택된 항목이 있을 경우 초기화한다.
          this.smartTraceFilter = {};
        }
        this.loadType = 'card';
        this.loadData();
      },
    },
    /**
     * 첫번째 카드 타입이 변경 시 카드들의 선택된 항목을 초기화한다.
     * @param  {[type]} type [description]
     * @return {[type]}      [description]
     * @author jda
     */
    // firstCardType() {
    //   this.resetSelectedItem();
    // },
    initSelectedItem: {
      deep: true,
      handler(initValue) {
        if (this.analysisType === 'netdevice') {
          return;
        }
        this.selectedItem = initValue;
      },
    },
    /**
     * 스마트 추적 filter바뀔 경우
     * 1. 전의 스마트 추적 필터와 같을 경우는 제외
     * 2. 스마트 추적 필터에 포함되어 있는 카드 제외
     * @param  {[type]} traceFilter [description]
     * @return {[type]}             [description]
     * @author jda
     */
    selectedTraceFilter(selectedTraceFilter) {
      const traceFilter = clone(selectedTraceFilter);
      if (isEqual(traceFilter, this.smartTraceFilter)) {
        return;
      }
      // [장비별 분석]에서 스마트 추적 이동시 선택된 항목이 아예 없다면 로드하지 않는다.
      if (!isEmpty(this.selectedItems) && objectIsEmpty(traceFilter)) {
        return;
      }

      const filterData = traceFilter[this.smartTraceFilterType];
      if (!objectIsEmpty(traceFilter) && !isEmpty(filterData)) {
        return;
      }

      this.loadType = 'trace';
      this.smartTraceFilter = traceFilter;
      this.loadData();
    },
  },
  methods: {
    /**
     * cardType을 이용하여 elesticsearch에서 사용하는 analysisFilter 데이터를 가공한다.
     * @author jda
     */
    setAnalysisFilterByCardType() {
      let cardType = this.cardType;
      if (this.showDirectionSwitch) {
        const direction = this.direction;

        switch (this.cardType) {
          case 'ipaddress': {
            if (direction === 'input') {
              cardType = 'srcIp';
            } else {
              cardType = 'dstIp';
            }
            break;
          }
          case 'port': {
            if (direction === 'input') {
              cardType = 'protocolSrcPort';
            } else {
              cardType = 'protocolDstPort';
            }
            break;
          }
          case 'netinterface': {
            if (direction === 'input') {
              cardType = 'deviceInput';
            } else {
              cardType = 'deviceOutput';
            }
            break;
          }
          default: {
            cardType = this.cardType;
            break;
          }
        }
      } else if (this.isConversation) {
        // conversation 카드가 IP카드 뒤에 올 경우
        const smartTraceFilter = this.smartTraceFilter;
        // console.log(smartTraceFilter);
        cardType = isEmpty(smartTraceFilter.dstIp) ? 'dstIp' : 'srcIp';
      }
      this.initSelectedItem.type = cardType;
      this.smartTraceFilterType = cardType;
    },
    /**
     * 스마트 추적 카드를 로드하기위한 파라메터를 가공한다.
     * flow추이차트의 파라메터 + cardType + smartTraceFilter
     * @author jda
     */
    setSmartTraceParams() {
      const flowParams = this.flowParams;
      if (isEmpty(flowParams) || isEmpty(flowParams.requestParamVO)) {
        return;
      }

      this.setAnalysisFilterByCardType();
      const cardType = this.smartTraceFilterType;

      let smartTraceFilter = this.smartTraceFilter;
      // [장비별 분석]에서 스마트 추적 선택항목이 있을 경우
      if (this.analysisType === 'netdevice' && !isEmpty(this.selectedItems)) {
        smartTraceFilter = omit(smartTraceFilter, cardType);
      }
      const requestParamVO = Object.assign({}, flowParams.requestParamVO, { cardType, smartTraceFilter });
      this.smartTraceParams = Object.assign({}, flowParams, { requestParamVO });
    },
    /**
     * 스마트 추적 카드 데이터를 로드한다.
     * @return {[type]} [description]
     * @author jda
     */
    loadData() {
      this.loading = true;
      this.setSmartTraceParams();

      if (this.analysisType !== 'netdevice') {
        this.resetSelectedItem();
      }

      const urlMethodName = this.requestUrl[this.cardType];
      FlowAPI[urlMethodName](this.smartTraceParams)
        .then((response) => {
          if (response.data.success) {
            this.loading = false;

            const result = response.data.data;
            this.traceData = result;

            // 전체 데이터 값 sum
            this.initSelectedItem.value = convertValuByUnit(sumBy(result, 'dataSum'), null, true, false);

            if (this.analysisType === 'netdevice') {
              const selectedItem = this.selectedItem;
              // 가공된 카드 타입(smartTraceFilterType)과 선택된 항목의 type이 같을 경우 선택 유지
              if (selectedItem.selected && this.smartTraceFilterType === selectedItem.type) {
                this.$nextTick(() => {
                  this.$refs.smartChart.setState('hover', selectedItem);
                });
              } else {
                this.resetSelectedItem();
              }
            } else {
              this.resetSelectedItem();
            }
            // if (this.analysisType !== 'netdevice') {
            //   this.resetSelectedItem();
            // } else {
            //   const selectedItem = this.selectedItem;
            //   console.log(selectedItem)
            //   if (selectedItem.selected && this.smartTraceFilterType === selectedItem.type) {
            //     this.$nextTick(() => {
            //       this.$refs.smartChart.setState('hover', selectedItem);
            //     });
            //   } else {
            //     this.resetSelectedItem();
            //   }
            // }
            this.$emit('selectItem', this, this.loadType);
          } else {
            this.$message(MsgUtil.getCustomResultMsgData('스마트 추적 데이터를 조회하지 못하였습니다.', false));
          }
        })
        .catch(() => {
          this.$message(MsgUtil.getCustomResultMsgData('스마트 추적 데이터를 조회하지 못하였습니다.', false));
        });
    },
    /**
     * 각 카드의 항목 선택 여부를 반환한다.
     * @param  {[type]} selectedItem [description]
     * @return {[type]}              [description]
     * @author jda
     */
    onSelectItem(selectedItem) {
      const initSelectedItem = clone(this.initSelectedItem);
      const chart = this.$refs.smartChart;

      if (isEmpty(chart)) {
        this.selectedItem = initSelectedItem;
        return;
      }

      if (this.selectedItem.itemId === selectedItem.itemId) {
        this.selectedItem = initSelectedItem;
        chart.setState();
      } else {
        this.selectedItem = {
          type: this.smartTraceFilterType,
          selected: true,
          name: selectedItem.name,
          value: selectedItem.selected ? selectedItem.value : convertValuByUnit(selectedItem.value, null, true, false), // 기존 선택된 데이터라면 value 바로 표현
          itemId: selectedItem.itemId,
        };
        this.$nextTick(() => {
          if (this.selectedItem.selected) {
            chart.setState('hover', selectedItem);
          }
        });
      }
      this.$emit('selectItem', this, this.loadType);
    },
    /**
     * 선택 항목을 초기화 한다.
     * @author jda
     */
    resetSelectedItem() {
      this.selectedItem = clone(this.initSelectedItem);
      this.$refs.smartChart.setState();
    },
    /**
     * 방향이 있는 카드들의 데이터 재로드
     * @return {[type]} [description]s
     * @author jda
     */
    onChangeCardDirection() {
      if (isEmpty(this.flowParams)) {
        return;
      }

      if (this.analysisType === 'netdevice') {
        this.smartTraceFilter = omit(this.smartTraceFilter, this.smartTraceParams.requestParamVO.cardType);
        this.resetSelectedItem();
      } else {
        this.smartTraceFilter = omit(this.smartTraceFilter, this.smartTraceFilterType);
      }
      this.loadData();
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/FlowAnalysis/components/view/SmartTraceComponent.vue